import React, { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logout from './auth/Logout';
import { SidebarItem } from '../pages/privatePages/PrivatePage';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import {
  TreeItem2,
  TreeItem2Content,
  TreeItem2Label,
  TreeItem2Props
} from '@mui/x-tree-view/TreeItem2';
import GenericIcons from './generics/GenericIcons';
import { styled } from '@mui/material/styles';
import { treeItemClasses } from '@mui/x-tree-view/TreeItem';
import '../css/PrivateSidebar.css';

interface CustomLabelProps {
  children: React.ReactNode;
  icon?: string;
  isShort?: boolean;
}

interface CustomContentProps {
  children: React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
  ischild?: 'y' | 'n'; // 'y' or 'n' as booleans are not valid attributes in the DOM.
}

interface CustomTreeItemProps extends TreeItem2Props {
  icon?: string;
  path?: string;
  isShort?: boolean;
  ischild?: 'y' | 'n';
  openSidebar?: () => void;
}

const getItemId = (
  items: Array<SidebarItem>,
  parentIds: Array<string> = []
): Array<{
  itemId: string;
  path: string;
  parentIds: Array<string>;
}> => {
  let res: Array<{
    itemId: string;
    path: string;
    parentIds: Array<string>;
  }> = [];
  items.forEach((item) => {
    const currentParentIds = [...parentIds, item.itemId];
    if (item.childOrComponent) {
      if (Array.isArray(item.childOrComponent)) {
        res = res.concat(getItemId(item.childOrComponent, currentParentIds));
      } else if (
        'path' in item.childOrComponent &&
        'render' in item.childOrComponent &&
        !item.disabled
      ) {
        res.push({ itemId: item.itemId, path: `/private${item.childOrComponent.path}`, parentIds });
      }
    }
  });
  return res;
};

const PrivateSideBar: FunctionComponent<{ sideBarItems: Array<SidebarItem> }> = ({
  sideBarItems
}) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [selectedItems, setSelectedItems] = useState<string | string[] | null>(null);

  useEffect(() => {
    const findLocation = getItemId(sideBarItems, []).find(
      (item) => item.path === location.pathname
    );
    if (findLocation) {
      setSelectedItems([findLocation.itemId, ...findLocation.parentIds]);
    } else {
      setSelectedItems(null);
    }
  }, [sideBarItems, location]);

  return isOpen ? (
    <FullSidebar
      sideBarItems={sideBarItems}
      closeSidebar={() => setIsOpen(false)}
      selectedItems={selectedItems}
    />
  ) : (
    <ShortSidebar
      sideBarItems={sideBarItems}
      openSidebar={() => setIsOpen(true)}
      selectedItems={selectedItems}
    />
  );
};

export default PrivateSideBar;

const CustomSimpleTreeView = styled(SimpleTreeView)(() => ({
  width: '100%'
}));

// Tiñe los íconos SVG del menú (grises) al cian oscuro de marca (#00708d).
const ACTIVE_ICON_FILTER =
  'brightness(0) saturate(100%) invert(23%) sepia(72%) saturate(1767%) hue-rotate(166deg) brightness(108%) contrast(101%)';

const ShortSidebar: FunctionComponent<{
  sideBarItems: Array<SidebarItem>;
  openSidebar: () => void;
  selectedItems: string | string[] | null;
}> = ({ sideBarItems, openSidebar, selectedItems }) => {
  return (
    <div className='private-short-sidebar-container'>
      <div className='vdr-app-sidebar-inner'>
        <div className='private-short-sidebar-arrow-inside-container'>
          <button
            type='button'
            className='vdr-app-sidebar-toggle'
            aria-label='Desplegar menú'
            onClick={() => openSidebar()}>
            <GenericIcons icon='doubleArrowLeftIcon' imageStyle={{ height: '100%' }} />
          </button>
        </div>
        <CustomSimpleTreeView selectedItems={selectedItems} multiSelect>
          {renderSideBarItem(sideBarItems, 1, true, openSidebar)}
        </CustomSimpleTreeView>
        <div className='private-sidebar-logout-container'>
          <Logout className='private-sidebar-logout' icon='signOutIcon' textLogout='' />
        </div>
      </div>
    </div>
  );
};

const FullSidebar: FunctionComponent<{
  sideBarItems: Array<SidebarItem>;
  closeSidebar: () => void;
  selectedItems: string | string[] | null;
}> = ({ sideBarItems, closeSidebar, selectedItems }) => {
  return (
    <div className='private-full-sidebar-container'>
      <div className='vdr-app-sidebar-inner'>
        <div className='private-full-sidebar-arrow-inside-container'>
          <span className='vdr-eyebrow vdr-app-sidebar-eyebrow' aria-hidden='true'>
            Menú
          </span>
          <button
            type='button'
            className='vdr-app-sidebar-toggle'
            aria-label='Plegar menú'
            onClick={() => closeSidebar()}>
            <GenericIcons icon='doubleArrowLeftIcon' imageStyle={{ height: '100%' }} />
          </button>
        </div>
        <CustomSimpleTreeView selectedItems={selectedItems} multiSelect>
          {renderSideBarItem(sideBarItems, 1)}
        </CustomSimpleTreeView>
        <div className='private-sidebar-logout-container'>
          <Logout className='private-sidebar-logout' icon='signOutIcon' textLogout='Cerrar sesión' />
        </div>
      </div>
    </div>
  );
};

// Componentes del ítem definidos fuera del render: si se recrean en cada render, React vuelve a
// montar el árbol y se pierden el foco y la expansión (con teclado "Administrador" no se abría).
const CustomContent = ({ disabled, selected, ischild, ...other }: CustomContentProps) => {
  return (
    <div style={{ position: 'relative' }}>
      <div
        style={
          selected && ischild === 'y'
            ? {
                position: 'absolute',
                height: '100%',
                width: '4px',
                top: 0,
                left: 0,
                zIndex: 1,
                backgroundColor: 'var(--vdr-cyan)'
              }
            : undefined
        }></div>
      <TreeItem2Content
        {...other}
        status={{
          expandable: true,
          expanded: false,
          focused: false,
          selected: false,
          disabled: !!disabled,
          editing: false,
          editable: false
        }}
      />
    </div>
  );
};

const CustomLabel = ({ icon, isShort, ...other }: CustomLabelProps) => {
  return (
    <div className='private-navbar-item-container'>
      {icon ? (
        <GenericIcons
          icon={icon}
          className='vdr-app-nav-icon'
          imageStyle={{ height: 18, width: 18 }}
        />
      ) : null}
      {!isShort ? <TreeItem2Label {...other} /> : null}
    </div>
  );
};

// Un estilo para la barra completa y otro para la plegada (antes dependía del closure de isShort).
const buildStyleTreeItem = (isShort: boolean) =>
  styled(TreeItem2)<CustomTreeItemProps>(({ path, ischild }) => {
    // Activo: fondo cian claro, barra cian de 4px a la izquierda, texto tinta e ícono cian oscuro.
    const activeRow = {
      backgroundColor: 'var(--vdr-cyan-tint)',
      boxShadow: 'inset 4px 0 0 var(--vdr-cyan)',
      [`& .${treeItemClasses.label}`]: {
        fontWeight: 700,
        color: 'var(--vdr-ink)'
      },
      '& .vdr-app-nav-icon': {
        filter: ACTIVE_ICON_FILTER
      }
    };
    return {
      color: 'var(--vdr-text)',
      width: '100%',
      [`& .${treeItemClasses.content}`]: {
        position: 'relative',
        minHeight: 44,
        margin: 0,
        padding: isShort ? '11px 0' : '10px 16px 10px 20px',
        gap: '12px',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: isShort ? 'center' : 'flex-start',
        borderRadius: 0,
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: 'var(--vdr-soft)'
        }
      },
      [`& .${treeItemClasses.label}`]: {
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontFamily: 'var(--vdr-font-display)',
        fontSize: 'clamp(14px, 1vw, 15px)',
        fontWeight: 600,
        lineHeight: 1.3,
        color: 'var(--vdr-text)'
      },
      [`& .${treeItemClasses.iconContainer}`]: {
        order: 3,
        marginLeft: 'auto',
        width: 18,
        color: 'var(--vdr-mute)',
        display: isShort ? 'none' : '',
        // Ítems sin hijos: sin flecha, el contenedor vacío no ocupa espacio.
        '&:empty': { display: 'none' },
        [`& .MuiSvgIcon-root`]: {
          width: 18,
          height: 18,
          aspectRatio: '1/1'
        }
      },
      [`& .${treeItemClasses.selected}`]:
        path && ischild === 'y'
          ? {
              ...activeRow,
              boxShadow: 'none'
            }
          : activeRow,
      [`& .${treeItemClasses.groupTransition}`]: {
        margin: '2px 0 6px 29px',
        padding: 0,
        borderLeft: '1px solid var(--vdr-line)',
        [`& .${treeItemClasses.content}`]: {
          minHeight: 40,
          paddingLeft: '20px'
        }
      }
    };
  });

const FullStyleTreeItem = buildStyleTreeItem(false);
const ShortStyleTreeItem = buildStyleTreeItem(true);

const CustomTreeItem = React.forwardRef<HTMLLIElement, CustomTreeItemProps>(
  ({ icon, path, ischild, isShort, disabled, openSidebar, ...other }, ref) => {
    const navigate = useNavigate();
    const location = useLocation();

    const selected = location.pathname === `/private${path}`;

    const handleActivate =
      isShort && openSidebar
        ? () => openSidebar()
        : path && !disabled
        ? () => navigate(`/private${path}`)
        : undefined;

    const CustomStyleTreeItem = isShort ? ShortStyleTreeItem : FullStyleTreeItem;

    return (
      <CustomStyleTreeItem
        disabled={disabled}
        onClick={handleActivate}
        // Siempre una función: un onKeyDown={undefined} explícito pisa el manejador de teclado del
        // árbol (MUI solo compone los manejadores que son funciones) y "Administrador" no se abría.
        onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) => {
          // Solo la tecla sobre este ítem (no la que sube desde un hijo).
          if (!handleActivate || event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') handleActivate();
        }}
        {...other}
        ref={ref}
        path={path}
        ischild={ischild ? ischild : 'n'}
        slots={{ label: CustomLabel, content: CustomContent }}
        slotProps={{
          label: { icon, isShort } as any,
          content: { disabled, ischild, selected } as any
        }}
      />
    );
  }
);

const renderSideBarItem = (
  sideBarItems: Array<SidebarItem>,
  level: number,
  isShort?: boolean,
  openSidebar?: () => void
): Array<JSX.Element | null> => {
  return sideBarItems.map((item, idx) => {
    if (Array.isArray(item.childOrComponent)) {
      return (
        <CustomTreeItem
          key={`sidebar-item-${idx}`}
          itemId={item.itemId}
          label={item.name}
          icon={item.icon}
          disabled={item.disabled}
          isShort={isShort}
          openSidebar={openSidebar}
          ischild={level !== 1 ? 'y' : 'n'}>
          {!isShort ? renderSideBarItem(item.childOrComponent, level + 1) : null}
        </CustomTreeItem>
      );
    } else if (typeof item.childOrComponent === 'object') {
      return (
        <CustomTreeItem
          key={`sidebar-item-${idx}`}
          itemId={item.itemId}
          label={item.name}
          icon={item.icon}
          path={item.childOrComponent.path}
          disabled={item.disabled}
          isShort={isShort}
          openSidebar={openSidebar}
          ischild={level !== 1 ? 'y' : 'n'}
        />
      );
    } else return null;
  });
};
